import { patientRegistryFetch } from "../api/patientRegistry";

/**
 * Lists checklists available in the user's currently-selected project.
 *
 * Per PRD-001: "the export configuration form requires the user to select
 * exactly one checklist from the list of checklists available in the
 * currently selected project." That set is sourced from
 * `GET /api/patient-registry/checklist?type=questionnaire` — the
 * patientRegistryFetch wrapper attaches both the Bearer JWT and the
 * `ProjectId` request header.
 */

export interface Checklist {
    id: string;
    name: string;
}

export class ChecklistFetchError extends Error {
    public readonly status: number;
    constructor(status: number, body: string) {
        super(`checklist fetch failed with status ${status}: ${body}`);
        this.name = "ChecklistFetchError";
        this.status = status;
    }
}

export async function fetchChecklists(): Promise<Checklist[]> {
    const response = await patientRegistryFetch("/checklist?type=questionnaire");
    if (!response.ok) {
        const body = await response.text().catch(() => "(unreadable body)");
        throw new ChecklistFetchError(response.status, body);
    }
    const data = (await response.json().catch(() => null)) as unknown;
    return normalize(data);
}

function normalize(data: unknown): Checklist[] {
    if (!Array.isArray(data)) return [];
    const out: Checklist[] = [];
    for (const item of data) {
        if (typeof item !== "object" || item === null) continue;
        const v = item as Record<string, unknown>;
        const id = typeof v.id === "string" ? v.id : null;
        const name = typeof v.name === "string" ? v.name : null;
        if (id && name) out.push({ id, name });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
}
